// Attention — the performance channel, running in Arcade (Order 16.6). This is
// Arcade's first consumption of anim-schema/attention.ts: the contract's three
// steerable channels (body / head / eyes) at its engagement levels, resolved
// every frame into the additive per-part offsets MeshPlayer lays over the baked
// clip. Where the character looks is runtime state (she tracks the player as he
// crosses the courtyard); HOW a body looks is these channels' speeds and
// magnitudes — eyes fast, head mid, body slow, exactly the grammar's ordering.
//
// SCALE HONESTY. At courtyard scale a standing figure is ~70 screen px and her
// head ~10; a pupil offset is sub-pixel. So the eyes channel — the contract's
// fast channel — resolves HERE into the head part's motion (the eyes lead the
// head by running the same aim at eyes speed), and the literal pupil offset is
// left to the close-up insert, where the film pipeline owns it. The body
// channel drives the torso at a fraction of the head's travel — the cascade's
// "as much of the body as the attitude demands", dialled for a standing figure
// whose stillness IS the acting.

import {
  ENGAGEMENT_SCALE,
  type EngagementLevel,
} from '@gooddesign/anim-schema';

/** Per-channel tracking speed, 1/s (exponential ease toward the aim). The
 *  grammar's ordering: eyes fast, head mid, body slow. */
const CHANNEL_SPEED = { eyes: 9, head: 3.2, body: 1.1 } as const;

/** Peak travel as a fraction of the rig's height, at engagement 'locked' and a
 *  full-lateral target. The head carries the look; the torso leans a shade.
 *  Ceiling: the rig's seam overlap is 3.5% of its height (build-woman-rig.py
 *  MARGIN) — head travel must stay under it or a full lock opens the neck. */
const HEAD_TRAVEL_X = 0.034;
const HEAD_TRAVEL_Y = 0.012;
const TORSO_TRAVEL_X = 0.012;
const TORSO_TRAVEL_Y = 0.004;

/** Which part keys the channels drive. A minimal rig maps 'head' and (optionally)
 *  'torso'; parts absent from the rig are simply not driven — the cascade uses
 *  as much of the body as the rig has. */
export interface AttentionRigMap {
  head?: number;
  torso?: number;
}

/** Find the channel-driven parts in a rig's paint-order part list. */
export function mapAttentionParts(partKeys: readonly string[]): AttentionRigMap {
  const map: AttentionRigMap = {};
  const head = partKeys.indexOf('head');
  const torso = partKeys.indexOf('torso');
  if (head >= 0) map.head = head;
  if (torso >= 0) map.torso = torso;
  return map;
}

export class AttentionChannel {
  private readonly map: AttentionRigMap;
  private readonly partCount: number;
  private readonly rigHeight: number;
  /** Eased aim per channel, -1..1 in yaw (x) and pitch (y). The eyes channel
   *  leads and the head/body follow it — running one aim at three speeds is
   *  what makes the cascade read. */
  private eyes = { x: 0, y: 0 };
  private head = { x: 0, y: 0 };
  private body = { x: 0, y: 0 };

  constructor(partKeys: readonly string[], rigHeight: number) {
    this.map = mapAttentionParts(partKeys);
    this.partCount = partKeys.length;
    this.rigHeight = rigHeight;
  }

  /** Advance the channels toward an aim and return MeshPlayer part offsets.
   *
   *  `yaw` is the target's lateral bearing in the figure's own facing space,
   *  -1..1 (negative = screen-left); `pitch` likewise vertical (positive =
   *  the target is below the gaze — she looks down at a near player). The
   *  caller derives these from world positions; this class only performs. */
  update(
    dtSec: number,
    yaw: number,
    pitch: number,
    engagement: EngagementLevel,
  ): Array<{ dx: number; dy: number } | null> {
    const scale = ENGAGEMENT_SCALE[engagement];
    const aimX = clamp1(yaw) * scale;
    const aimY = clamp1(pitch) * scale;

    ease(this.eyes, aimX, aimY, CHANNEL_SPEED.eyes * dtSec);
    ease(this.head, aimX, aimY, CHANNEL_SPEED.head * dtSec);
    ease(this.body, aimX, aimY, CHANNEL_SPEED.body * dtSec);

    const offsets: Array<{ dx: number; dy: number } | null> = new Array(this.partCount).fill(null);
    if (this.map.head !== undefined) {
      // The head blends the eyes' fast break with its own slower carry — the
      // flick you notice first, settling into the turn.
      const hx = this.head.x * 0.75 + this.eyes.x * 0.25;
      const hy = this.head.y * 0.75 + this.eyes.y * 0.25;
      offsets[this.map.head] = {
        dx: hx * HEAD_TRAVEL_X * this.rigHeight,
        dy: hy * HEAD_TRAVEL_Y * this.rigHeight,
      };
    }
    if (this.map.torso !== undefined) {
      offsets[this.map.torso] = {
        dx: this.body.x * TORSO_TRAVEL_X * this.rigHeight,
        dy: this.body.y * TORSO_TRAVEL_Y * this.rigHeight,
      };
    }
    return offsets;
  }
}

const clamp1 = (v: number) => (v < -1 ? -1 : v > 1 ? 1 : v);

function ease(state: { x: number; y: number }, aimX: number, aimY: number, k: number): void {
  const f = 1 - Math.exp(-k);
  state.x += (aimX - state.x) * f;
  state.y += (aimY - state.y) * f;
}
